/**
 * RecentMattersStrip — Cluster H.6 (Feb 2026).
 *
 * Horizontal strip of the 3 most-recently-touched matters owned by the
 * caller. Rendered above the composer in the WelcomeHero empty state,
 * so users can resume work in one click instead of scrolling the
 * sidebar.
 *
 * Source: GET /api/v2/agent/dashboard/recent_matters?limit=3.
 * Each card → react-router navigate('/chat/{session_id}') — the
 * canonical deep-link from Cluster E.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function relativeTime(iso, isArabic) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const sec = Math.max(0, Math.floor((now - then) / 1000));
    if (sec < 60)      return isArabic ? 'الآن' : 'just now';
    if (sec < 3600)    { const m = Math.floor(sec / 60);  return isArabic ? `منذ ${m} د` : `${m}m ago`; }
    if (sec < 86400)   { const h = Math.floor(sec / 3600); return isArabic ? `منذ ${h} س` : `${h}h ago`; }
    if (sec < 604800)  { const d = Math.floor(sec / 86400);return isArabic ? `منذ ${d} ي` : `${d}d ago`; }
    return new Date(iso).toLocaleDateString(isArabic ? 'ar' : 'en');
  } catch { return ''; }
}

const STATUS_LABELS = {
  draft_in_progress: { en: 'Draft in progress', ar: 'مسودة قيد العمل' },
  awaiting_review:   { en: 'Awaiting review',   ar: 'بانتظار المراجعة' },
  sent:              { en: 'Sent',              ar: 'مرسلة' },
  done:              { en: 'Ready',             ar: 'جاهزة' },
};

const STATUS_COLORS = {
  draft_in_progress: '#d6b27d',
  awaiting_review:   '#b27dd6',
  sent:              '#7db2d6',
  done:              '#7dd6a3',
};

export const RecentMattersStrip = ({ isArabic }) => {
  const { session } = useAuth();
  const token = session?.access_token;
  const navigate = useNavigate();
  const [matters, setMatters] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token) { setMatters([]); setLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/v2/agent/dashboard/recent_matters?limit=3&lang=${isArabic ? 'ar' : 'en'}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000,
        });
        if (!cancelled) {
          setMatters(res.data?.matters || []);
          setLoaded(true);
        }
      } catch (_e) {
        if (!cancelled) { setMatters([]); setLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [token, isArabic]);

  if (!loaded) return null;
  if (matters.length === 0) return null;

  return (
    <div
      data-testid="recent-matters-strip"
      style={{ width: '100%', maxWidth: 680, margin: '0 0 16px' }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <div
        style={{
          // AA bump: #555 ≈ 7.5:1 on white. Was #888 (3.5:1 FAIL).
          fontSize: 11,
          color: '#555555',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          marginBottom: 8,
          textAlign: isArabic ? 'right' : 'left',
        }}
        data-testid="recent-matters-strip-title"
      >
        {isArabic ? 'مسائل حديثة' : 'Recent matters'}
      </div>
      <div
        style={{
          display: 'grid',
          // I.A — Cluster I (Feb 2026): was hard-coded `1fr 1fr 1fr`,
          // which produced 110px-wide cards at 430px viewport with
          // truncated titles. `auto-fit` lets the strip reflow into 1
          // or 2 columns on phones; 3 on ≥ 680px containers.
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 8,
        }}
      >
        {matters.map((m) => (
          <button
            key={m.session_id}
            type="button"
            onClick={() => navigate(`/chat/${encodeURIComponent(m.session_id)}`)}
            data-testid={`recent-matter-card-${m.session_id}`}
            style={{
              textAlign: isArabic ? 'right' : 'left',
              background: '#F5F5F3',
              border: '1px solid #E0E0E0',
              borderRadius: 6,
              padding: '10px 12px',
              cursor: 'pointer',
              transition: 'border-color 0.15s, background 0.15s',
              fontFamily: 'inherit',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              minHeight: 78,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#bbb'; e.currentTarget.style.background = '#fafaf8'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E0E0E0'; e.currentTarget.style.background = '#F5F5F3'; }}
          >
            <div
              style={{
                fontSize: 13,
                color: '#222',
                fontWeight: 500,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {m.title}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 10,
                // AA: #5e5e5e ≈ 6.7:1 on #F5F5F3 (was #888 ≈ 3.5:1 FAIL)
                color: '#5e5e5e',
                marginTop: 'auto',
                flexDirection: isArabic ? 'row-reverse' : 'row',
              }}
            >
              <span
                data-testid={`recent-matter-status-${m.session_id}`}
                style={{
                  display: 'inline-block',
                  width: 6, height: 6, borderRadius: 3,
                  background: STATUS_COLORS[m.status] || '#888',
                  flexShrink: 0,
                }}
                aria-label={STATUS_LABELS[m.status]?.[isArabic ? 'ar' : 'en'] || m.status}
              />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.doc_type}
              </span>
              <span style={{ color: '#666' }}>{relativeTime(m.last_edited_at, isArabic)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default RecentMattersStrip;
